// apps/client/src/game/commands/core.ts — the core commands every screen and system calls (§8.7 CoreCommands)
import type { ActionReq, ActionType, SpaceId, TileXY, TownId } from '@whiskerton/shared';
import { MissingManifestError } from '../../assets/service';
import { IS_DEV, IS_E2E } from '../../boot/env';
import type { ActResult, Silent } from '../../net/actions';
import type { ClientSettings } from '../../state/slices/core';
import { useGame } from '../../state/store';
import type { GameStore } from '../../state/types';
import type { CommandFactory, Game } from '../game';
import { installTestHooks } from '../testHooks';

export interface CoreCommands {
  act<T extends ActionType>(type: T, req: ActionReq<T>, opts?: { optimistic?: () => () => void; silent?: Silent }): Promise<ActResult<T>>;   // silent: no reject toast, or none for the listed codes (G13)
  enterTown(townId: TownId): Promise<void>;
  leaveTown(): void;
  enterSpace(to: SpaceId): Promise<void>;                // door fade + space.enter
  walkTo(tile: TileXY, then?: () => void): void;
  press(button: 'A' | 'B' | 'tool'): void;               // 'tool' emits bus 'press:tool' (M1-T13 cycles rod/shovel)
  setQuality(q: ClientSettings['quality']): void;
  setVolumes(v: { music?: number; sfx?: number }): void;
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameCommands
declare module '../game' { interface GameCommands extends CoreCommands {} }

/** DEV only, when the kits were never built: a plain note for the developer (not player-facing text). */
function missingAssetsNote(): void {
  if (document.getElementById('missing-assets')) return;
  const el = document.createElement('div');
  el.id = 'missing-assets';
  el.className = 'hit';
  el.style.cssText = 'position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:60;max-width:440px;padding:22px 26px;'
    + 'background:#FFFBF1;border-radius:22px;box-shadow:0 18px 32px -18px rgba(74,58,44,.5),0 2px 6px rgba(74,58,44,.1);'
    + 'font:800 16px/1.45 Nunito,system-ui,sans-serif;color:#4A3A2C;cursor:pointer;';
  el.textContent = 'The 3D kits are not built yet. Run `pnpm assets`, then reload. (Tap to dismiss.)';
  el.addEventListener('click', () => el.remove());
  document.body.append(el);
}
/** The world slice as it was before any town (and no reconnect banner). */
const worldReset = (): Partial<GameStore> => ({ townId: null, town: null, residents: {}, presence: {}, you: null, space: 'out', map: null, prompt: null,
  socket: 'idle', banner: null, clock: { minuteOfDay: 0, weekday: 0, dayKey: null } });

export const commands: CommandFactory<CoreCommands> = (game: Game) => {
  const cmd: CoreCommands = {
    act(type, req, opts) {
      return game.socket ? game.socket.actions.send(type, req, opts) : Promise.resolve({ ok: false, code: 'offline' });
    },
    async enterTown(townId) {
      const sock = game.socket;
      if (sock && sock.town === townId && sock.state !== 'closed' && sock.state !== 'updateRequired') return;   // already there (or on the way)
      if (sock) cmd.leaveTown();
      const s = useGame.getState();
      s.closeAll();
      s.setTownsNotice(null);                                     // the towns screen's "you were removed" note has been seen
      s.setPhase('connecting');
      useGame.setState({ townId });
      game.attach();
      try { await game.assets.ready(); }
      catch (e) {
        if (e instanceof MissingManifestError && IS_DEV) missingAssetsNote();
        else useGame.getState().toast('errors.server_error', undefined, 'warn');
        if (useGame.getState().phase === 'connecting') { useGame.setState(worldReset()); useGame.getState().setPhase('towns'); }
        return;
      }
      if (useGame.getState().phase !== 'connecting' || useGame.getState().townId !== townId) return;   // left (or signed out) meanwhile
      if (game.socket?.town === townId) return;                  // a second enterTown(townId) during the await already connected
      game.connect(townId);
      if (IS_DEV || IS_E2E) installTestHooks(game);
    },
    leaveTown() {
      game.teardown();
      useGame.setState(worldReset());
      const s = useGame.getState();
      s.closeAll();
      if (s.phase === 'inTown' || s.phase === 'connecting') s.setPhase('towns');   // never over signedOut (a 401 got there first)
    },
    async enterSpace(to) {
      try {
        await game.holdMovement(async () => {
          const r = await cmd.act('space.enter', { to });
          if (r.ok) await game.switchSpace(r.r.space, r.r.spawn);
        });
      } catch (e) {                                              // the new scene failed to build; callers fire and forget (`void`)
        if (IS_DEV) console.error('space switch failed', e);
        useGame.getState().toast('errors.server_error', undefined, 'warn');
      }
    },
    walkTo(tile, then) { game.walkTo(tile, then); },
    press(button) {
      if (button === 'A') game.interactions.runBest();
      else if (button === 'B') game.tapToWalk.cancel();
      else game.bus.emit('press:tool', undefined);
    },
    setQuality(q) {
      useGame.getState().setSettings({ quality: q });
      game.applyQuality(q);
    },
    setVolumes(v) {
      useGame.getState().setSettings(v);
      game.sound.setVolumes(v);
    },
  };
  return cmd;
};
